<template>
	<div class="kanban-column">
		<!-- DCTL-2257: header chrome comes from the column's workflow state, not a
		per-board hex - one map for every board (constants/workflowStates.js). The
		wash is the state's OPAQUE tint token rather than the old base + "18" alpha
		suffix (COLOR.md rule 4), and the icon and count take `-ink`, which is the
		only member of the triplet that clears 4.5:1 as text.

		DCTL-2415: `rounded-t` stays at 4px on purpose - do NOT "correct" it to
		rounded-t-lg to match .kanban-column's 8px. The column's overflow clips
		this header to the column's PADDING box, which curves at 8px minus the 1px
		border = 7px. A header rounded to 8px is tighter than that clip and leaves
		a crescent of the column's white background showing; 4px is squarer than
		the clip, so the clip alone shapes the corner. Measured at 20x: 8px = 1134
		stray white subpixels per corner, 4px = 0. -->
		<div
			class="kanban-column__header pa-3 rounded-t"
			:class="`bg-${state.tint}`"
		>
			<div class="d-flex align-center">
				<v-icon :color="state.ink" size="small" class="mr-2">
					{{ column.icon }}
				</v-icon>
				<span class="text-subtitle-2 font-weight-bold">
					{{ column.title }}
				</span>
				<v-chip
					size="x-small"
					variant="flat"
					class="ml-2 bg-surface"
					:class="`text-${state.ink}`"
				>
					{{ count }}
				</v-chip>
			</div>
		</div>
		<div class="kanban-column__body pa-2">
			<template v-if="items.length > 0">
				<!-- DCTL-2195: dataEntryUuid first - the SDE board can now show
				several cards for one paper (one per owned extraction), so keying on
				paperUuid alone would collide. Falls back to paperUuid / qcUuid for
				cards with no extraction (assigned, available) and the other boards. -->
				<div
					v-for="(item, index) in items"
					:key="item.dataEntryUuid || item.paperUuid || item.qcUuid || index"
				>
					<slot name="card" :item="item" />
				</div>
			</template>
			<div v-else-if="loading" class="d-flex justify-center pa-4">
				<v-progress-circular indeterminate color="primary" size="32" />
			</div>
			<div v-else class="text-center text-grey-darken-2 pa-4">
				<v-icon size="large" color="grey-lighten-1"> mdi-inbox-outline </v-icon>
				<div class="text-body-2 mt-1">No items</div>
			</div>
			<slot name="footer" />
		</div>
	</div>
</template>

<script>
import { workflowState } from "@/constants/workflowStates";

export default {
	name: "KanbanColumn",

	props: {
		column: {
			// { id, title, icon, state } - `state` is a key of WORKFLOW_STATES and
			// is what colours the header. DCTL-2279: a merged column (the SDE
			// board's "My Papers", the QC board's "In Progress") names NEUTRAL_STATE
			// instead. It used to name its "dominant" state, but a column whose
			// members span buckets has no dominant one to name, and REV 2.1.1 ruled
			// out the custody reasoning that picked one. The cards inside carry their
			// own per-state chips, which is where a hue can be true.
			type: Object,
			required: true,
		},
		items: {
			type: Array,
			default: () => [],
		},
		count: {
			type: Number,
			default: 0,
		},
		// The column's cards are being fetched: an empty column shows a spinner
		// instead of "No items". Cards already shown stay put.
		loading: {
			type: Boolean,
			default: false,
		},
	},

	emits: ["card-action"],

	computed: {
		// Falls back to neutral chrome for a column with no workflow state, so a
		// board that is not on the axis still renders.
		state() {
			return workflowState(this.column.state);
		},
	},
};
</script>

<style scoped lang="scss">
.kanban-column {
	min-width: 280px;
	background: rgb(var(--v-theme-surface));
	border: 1px solid var(--border-default);
	border-radius: 8px;
	// DCTL-2415: clips the header's opaque state tint to the column's own curve.
	// Without this the header's square-ish 4px corner spills PAST the column's 8px
	// curve, and because an in-flow child paints after its parent's border, the
	// tint paints OVER the border arc and erases it - measured at 20x, 24% of the
	// arc goes missing at each top corner (7632 border px vs 10104 clipped). On a
	// near-white tint like bg-surface-recessed that reads as a pale gap in the
	// outline, which is how it was reported. Latent until DCTL-2257 gave the
	// header a background of its own; before that the header painted surface white
	// over a surface-white corner and the spill was invisible.
	//
	// Safe to clip: no board here has drag-and-drop (no draggable / dragstart /
	// vuedraggable / @drop / dragover in components/kanban or in any of the three
	// <kanban-board> call sites), and `&__body` keeps its own overflow-y.
	overflow: hidden;
	display: flex;
	flex-direction: column;
	height: 100%;

	&__body {
		flex: 1;
		overflow-y: auto;
		max-height: calc(100vh - 320px);
	}
}
</style>
